import { useEffect } from "react";

/**
 * Per-page canonical / title / OG / Twitter / JSON-LD manager for the SPA.
 *
 * URL convention (must match /public/sitemap.xml exactly):
 *   • Homepage:  https://binshare.co.uk/       (trailing slash)
 *   • All other: https://binshare.co.uk/<path> (no trailing slash)
 *   • Always https, always the apex (no www)
 */

const SITE_ORIGIN = "https://binshare.co.uk";

function canonicalFor(path) {
  if (!path || path === "/" || path === "") return `${SITE_ORIGIN}/`;
  const normalised = path.replace(/\/+$/, "");
  return `${SITE_ORIGIN}${normalised}`;
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertMeta(attr, name, content) {
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertJsonLd(id, data) {
  if (!data) return;
  let el = document.head.querySelector(`script[data-jsonld-id="${id}"]`);
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("data-jsonld-id", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function removeJsonLdMatching(prefix) {
  document.head
    .querySelectorAll(`script[data-jsonld-id^="${prefix}"]`)
    .forEach((el) => el.remove());
}

export default function SEOHead({
  path,
  title,
  description,
  image = `${SITE_ORIGIN}/icon-512.png`,
  ogType = "website",
  keywords = null,
  jsonLd = null,
}) {
  useEffect(() => {
    const canonical = canonicalFor(path);

    if (title) document.title = title;

    upsertLink("canonical", canonical);

    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:type", ogType);
    upsertMeta("property", "og:site_name", "Binshare");
    if (title) upsertMeta("property", "og:title", title);
    if (description) upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:image", image);

    upsertMeta("name", "twitter:card", "summary_large_image");
    if (title) upsertMeta("name", "twitter:title", title);
    if (description) upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image);

    if (description) upsertMeta("name", "description", description);
    if (keywords) upsertMeta("name", "keywords", keywords);

    // Multi-block JSON-LD support: pass a single object or an array.
    removeJsonLdMatching("page-");
    if (jsonLd) {
      const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      blocks.forEach((b, i) => upsertJsonLd(`page-${i}`, b));
    }

    return () => {
      removeJsonLdMatching("page-");
    };
  }, [path, title, description, image, ogType, keywords, jsonLd]);

  return null;
}
